import React, { useCallback, useEffect, useState } from 'react';
import TreeItem, { TreeItemContentProps, TreeItemProps, useTreeItem } from '@mui/lab/TreeItem';
import TreeView from '@mui/lab/TreeView';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChildDepartments, Department } from '@/types/Department.type';
import URL from '@/common/routes/Url';
import clsx from 'clsx';
import { Typography } from '@mui/material';

type Props = {
  departments: Array<ChildDepartments & Department>;
};

const DepartmentListItems: React.FC<Props> = ({ departments }) => {
  const [expanded, setExpanded] = React.useState<string[]>([]);
  const navigate = useNavigate();

  const handleExpandClick = () => {
    setExpanded(expanded.length === 0 ? departments.map((v) => String(v.id)) : []);
  };

  const handleToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
    setExpanded(nodeIds);
  };

  const clickHandler = useCallback((event: React.MouseEvent, id: string): void => {
    event.stopPropagation();
    navigate(`${URL.actPlan}?departmentId=${id}`);
  }, []);

  const [selected, setSelected] = useState<Array<string> | undefined>([]);
  const [searchParams] = useSearchParams();

  const handleSelect = (event: React.SyntheticEvent, nodeIds: Array<string>) => {
    setSelected(nodeIds);
  };

  useEffect(() => {
    if (departments.length === 0) {
      return;
    }
    if (searchParams.get('departmentId')) {
      const SelectedId: string = searchParams.get('departmentId') as string;
      setSelected([SelectedId]);
      departments.map((parent) => {
        parent.childDepartments &&
          parent.childDepartments.map((child) => {
            if (String(child.id) == SelectedId) {
              expanded.push(String(parent.id));
              setExpanded(expanded);
            }
          });
      });
    }
    if (searchParams.get('myGroupId')) {
      setSelected([]);
    }
  }, [departments, searchParams.get('departmentId'), searchParams.get('myGroupId')]);

  return (
    <Box>
      <Box sx={{ height: 30, flexGrow: 1, maxWidth: 400, pl: 1 }}>
        <Box sx={{ mb: 1 }}>
          <Button onClick={handleExpandClick}>{expanded.length === 0 ? 'OPEN' : 'CLOSE'}</Button>
        </Box>
      </Box>
      <TreeView
        aria-label="file system navigator"
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
        expanded={expanded}
        onNodeToggle={handleToggle}
        onNodeSelect={handleSelect}
        selected={selected}
      >
        {departments.map((parent) => (
          <CustomTreeItem
            key={parent.id}
            nodeId={String(parent.id)}
            label={parent.shortName}
            sx={{ marginBottom: '0.1rem' }}
          >
            {parent.childDepartments &&
              parent.childDepartments.map((child) => (
                <TreeItem
                  key={child.id}
                  nodeId={String(child.id)}
                  label={child.shortName}
                  onClick={(e) => clickHandler(e, String(child.id))}
                  sx={{ marginTop: '0.1rem' }}
                />
              ))}
          </CustomTreeItem>
        ))}
      </TreeView>
    </Box>
  );
};

const CustomContent = React.forwardRef(function CustomContent(props: TreeItemContentProps, ref) {
  const navigate = useNavigate();

  const { classes, className, label, nodeId, icon: iconProp, expansionIcon, displayIcon } = props;

  const { disabled, expanded, selected, focused, handleExpansion, handleSelection, preventSelection } =
    useTreeItem(nodeId);

  const icon = iconProp || expansionIcon || displayIcon;

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    preventSelection(event);
  };

  const handleExpansionClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    handleExpansion(event);
  };

  const handleSelectionClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    navigate(`${URL.actPlan}?departmentId=${nodeId}`);
    handleSelection(event);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={clsx(className, classes.root, {
        [classes.expanded]: expanded,
        [classes.selected]: selected,
        [classes.focused]: focused,
        [classes.disabled]: disabled,
      })}
      onMouseDown={handleMouseDown}
      ref={ref as React.Ref<HTMLDivElement>}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div onClick={handleExpansionClick} className={classes.iconContainer}>
        {icon}
      </div>
      <Typography onClick={handleSelectionClick} component="div" className={classes.label}>
        {label}
      </Typography>
    </div>
  );
});

function CustomTreeItem(props: TreeItemProps) {
  return <TreeItem ContentComponent={CustomContent} {...props} />;
}

export default DepartmentListItems;
